import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Paperclip, Trash, Download, Loader2 } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function ContractEditModal({ contract, isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    contract_type: '',
    valor_recorrente: 0,
    billing_frequency: 'mensal',
    start_date: '',
    duration_months: 12,
    status: 'ativo',
    escopo_resumido: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (contract) {
      setFormData({
        title: contract.title || '',
        contract_type: contract.contract_type || '',
        valor_recorrente: contract.valor_recorrente || 0,
        billing_frequency: contract.billing_frequency || 'mensal',
        start_date: contract.start_date ? new Date(contract.start_date).toISOString().split('T')[0] : '',
        duration_months: contract.duration_months || 0,
        status: contract.status || 'ativo',
        escopo_resumido: contract.escopo_resumido || '',
        notes: contract.notes || ''
      });
      setAttachments(contract.attachments || []);
    }
  }, [contract]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      };

      await api.put(`/contracts/${contract.id}`, payload);
      toast.success('Contrato atualizado com sucesso!');
      onSuccess();
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erro ao atualizar contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Max 15MB
    if (file.size > 15 * 1024 * 1024) {
      toast.error('O arquivo deve ter no máximo 15MB');
      return;
    }

    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    
    try {
      const { data } = await api.post(`/contracts/${contract.id}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Anexo adicionado!');
      if (data.attachment) {
        setAttachments(prev => [...prev, data.attachment]);
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Erro ao enviar anexo');
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await api.get(`/contracts/${contract.id}/attachments/${attachment.id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      toast.error('Erro ao baixar o anexo');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!confirm('Deseja realmente remover este anexo?')) return;
    try {
      await api.delete(`/contracts/${contract.id}/attachments/${attachmentId}`);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Anexo removido');
    } catch (err) {
      toast.error('Erro ao remover anexo');
    }
  };

  if (!contract) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Contrato</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Título do Contrato *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="edit-contract-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo de Contrato *</Label>
              <Select
                value={formData.contract_type}
                onValueChange={(value) => setFormData({ ...formData, contract_type: value })}
              >
                <SelectTrigger data-testid="edit-contract-type">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="marketing_digital">Marketing Digital</SelectItem>
                  <SelectItem value="social_media">Social Media</SelectItem>
                  <SelectItem value="trafego_pago">Tráfego Pago</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="consultoria">Consultoria</SelectItem>
                  <SelectItem value="branding">Branding</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="edit-contract-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="pausado">Pausado</SelectItem>
                  <SelectItem value="encerrado">Encerrado</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_valor_recorrente">Valor Mensal (R$) *</Label>
              <Input
                id="edit_valor_recorrente"
                type="number"
                min="0"
                step="0.01"
                value={formData.valor_recorrente}
                onChange={(e) => setFormData({ ...formData, valor_recorrente: parseFloat(e.target.value) || 0 })}
                required
                data-testid="edit-contract-value"
              />
            </div>
            <div>
              <Label>Frequência</Label>
              <Select
                value={formData.billing_frequency}
                onValueChange={(value) => setFormData({ ...formData, billing_frequency: value })}
              >
                <SelectTrigger data-testid="edit-billing-freq">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensal">Mensal</SelectItem>
                  <SelectItem value="trimestral">Trimestral</SelectItem>
                  <SelectItem value="semestral">Semestral</SelectItem>
                  <SelectItem value="anual">Anual</SelectItem>
                  <SelectItem value="avulso">Avulso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_start_date">Data de Início *</Label>
              <Input
                id="edit_start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
                data-testid="edit-contract-start-date"
              />
            </div>
            <div>
              <Label htmlFor="edit_duration">Duração (meses) *</Label>
              <Input
                id="edit_duration"
                type="number"
                min="1"
                value={formData.duration_months}
                onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) || null })}
                required
                data-testid="edit-contract-duration"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="edit_escopo">Escopo Resumido</Label>
            <textarea
              id="edit_escopo"
              className="w-full min-h-[60px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              value={formData.escopo_resumido}
              onChange={(e) => setFormData({ ...formData, escopo_resumido: e.target.value })}
              data-testid="edit-contract-escopo"
            />
          </div>

          <div>
            <Label htmlFor="edit_notes">Observações</Label>
            <textarea
              id="edit_notes"
              className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              data-testid="edit-contract-notes"
            />
          </div>

          <div>
            <Label>Anexos</Label>
            <div className="mt-2 space-y-2">
              {attachments.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-2 border rounded-md bg-secondary/20">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm truncate">{file.filename}</span>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(file)}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDeleteAttachment(file.id)}>
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              <div className="flex items-center gap-2 pt-2">
                <Button type="button" variant="outline" className="relative overflow-hidden w-full" disabled={uploading}>
                  {uploading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</>
                  ) : (
                    <><Paperclip className="mr-2 h-4 w-4" /> Adicionar Anexo (Max 15MB)</>
                  )}
                  <input
                    type="file"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    title="Adicionar Arquivo"
                  />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || !formData.contract_type} data-testid="submit-edit-contract-btn">
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
